import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Stack,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import { ratingApi } from '../../services/api'; // This will be created later

const RatingSchemeDialog = ({ open, onClose, mode, assetTypeId, scheme, userData, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    auto_update_from_inspection: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && scheme) {
        setFormData({
          name: scheme.name || '',
          description: scheme.description || '',
          auto_update_from_inspection: scheme.auto_update_from_inspection || false
        });
      } else {
        setFormData({
          name: '',
          description: '',
          auto_update_from_inspection: false
        });
      }
      setError(null);
    }
  }, [open, mode, scheme]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      auto_update_from_inspection: e.target.checked
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Rating scheme name is required');
      return;
    }

    setLoading(true);
    setError(null);

    const schemeData = {
      ...formData,
      organization_id: userData.organizationId,
      asset_type_id: assetTypeId,
    };

    try {
      if (mode === 'create') {
        await ratingApi.createScheme(schemeData);
      } else {
        await ratingApi.updateScheme(scheme.id, schemeData);
      }
      onUpdate(assetTypeId);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${mode} rating scheme.`);
      console.error(`Error ${mode}ing rating scheme:`, err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.name.trim().length > 0;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'success.main', color: 'success.contrastText', display: 'flex', alignItems: 'center', gap: 1 }}>
        <StarIcon />
        {mode === 'create' ? 'Create' : 'Edit'} Rating Scheme
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
            
            <TextField
              label="Rating Scheme Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              fullWidth
              placeholder="e.g., Asset Condition Rating, Performance Scale"
              helperText="Enter a descriptive name for this rating scheme"
              disabled={loading}
            />
          </Box>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Describe the rating scale and criteria for asset evaluation..."
            helperText="Optional: Provide details about the rating methodology"
            disabled={loading}
          />

          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.auto_update_from_inspection}
                  onChange={handleCheckboxChange}
                  name="autoUpdate"
                  color="primary"
                  disabled={loading}
                />
              }
              label="Auto-update asset rating from latest inspection"
            />
            <FormHelperText sx={{ ml: 2 }}>
              If checked, an asset's rating will be automatically set by its most recent 'rated' inspection. If unchecked, the rating must be set manually on the asset details page.
            </FormHelperText>
          </FormControl>

          {/* Preview information */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Name:</strong> {formData.name || 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Organization:</strong> {userData?.organizationName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Asset Type ID:</strong> {assetTypeId}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!isFormValid() || loading}
          color="success"
          sx={{ px: 3 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            mode === 'create' ? 'Create Rating Scheme' : 'Update Rating Scheme'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RatingSchemeDialog;